import {BatchItem} from './bulkVosTypes'
import {formatSubmitted} from './bulkVosClientPortalFormat'

export type BulkVosFinanceBatchTab = 'pending' | 'approved'

/** Matches BulkVosBatchStatus: Open=0, ClientSubmitted=1, FinanceApproved=2 */
export type BulkVosFinanceBatchOption = {
  batchId: number
  customerId: number
  customerName?: string
  disputedCount?: number
  status: number
  /** UTC created timestamp when provided by list API (for default batch pick). */
  createdAtUtc?: string
  submittedAtUtc?: string
}

export type BulkVosFinanceFilterData = {
  customerIds: string[]
  batchTab: BulkVosFinanceBatchTab
  batchId: number | null
}

export const emptyBulkVosFinanceFilter = (): BulkVosFinanceFilterData => ({
  customerIds: [],
  batchTab: 'pending',
  batchId: null,
})

function financeFilterCustomerIdsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].map((id) => String(id).trim()).sort()
  const sortedB = [...b].map((id) => String(id).trim()).sort()
  return sortedA.every((id, i) => id === sortedB[i])
}

export function financeFiltersEqual(
  a: BulkVosFinanceFilterData,
  b: BulkVosFinanceFilterData,
): boolean {
  return (
    a.batchTab === b.batchTab &&
    a.batchId === b.batchId &&
    financeFilterCustomerIdsEqual(a.customerIds, b.customerIds)
  )
}

function batchRecencyScore(b: BulkVosFinanceBatchOption): number {
  if (b.createdAtUtc) {
    const ts = Date.parse(b.createdAtUtc)
    if (Number.isFinite(ts)) return ts
  }
  return b.batchId
}

export function pickLatestBatchForTab(
  batches: BulkVosFinanceBatchOption[],
  tab: BulkVosFinanceBatchTab,
): BulkVosFinanceBatchOption | null {
  const tabBatches = batchesForFinanceTab(batches, tab)
  if (!tabBatches.length) return null
  return tabBatches.reduce((latest, b) =>
    batchRecencyScore(b) > batchRecencyScore(latest) ? b : latest,
  )
}

export function batchMatchesFinanceTab(
  batch: BulkVosFinanceBatchOption,
  tab: BulkVosFinanceBatchTab,
): boolean {
  if (tab === 'approved') return batch.status === 2
  return batch.status === 0 || batch.status === 1
}

/** Initial / reset selection: latest pending batch (any customer), else latest approved. */
export function buildDefaultFinanceFilter(
  batches: BulkVosFinanceBatchOption[],
  orderedPending?: BulkVosFinanceBatchOption[],
): BulkVosFinanceFilterData {
  const pending =
    orderedPending?.find((b) => batchMatchesFinanceTab(b, 'pending')) ??
    pickLatestBatchForTab(batches, 'pending')
  if (pending) {
    return {
      batchTab: 'pending',
      batchId: pending.batchId,
      customerIds: [],
    }
  }
  const approved = pickLatestBatchForTab(batches, 'approved')
  if (approved) {
    return {
      batchTab: 'approved',
      batchId: approved.batchId,
      customerIds: [],
    }
  }
  return emptyBulkVosFinanceFilter()
}

/** Align tab/customer filters with a chosen batch so detail load is not cleared. */
export function normalizeFinanceFilterSelection(
  filter: BulkVosFinanceFilterData,
  batches: BulkVosFinanceBatchOption[],
): BulkVosFinanceFilterData {
  if (filter.batchId == null) return filter
  const row = batches.find((b) => b.batchId === filter.batchId)
  if (!row) {
    return {...filter, batchId: null}
  }

  let batchTab = filter.batchTab
  if (!batchMatchesFinanceTab(row, batchTab)) {
    batchTab = row.status === 2 ? 'approved' : 'pending'
  }

  let customerIds = filter.customerIds
  if (
    customerIds.length > 0 &&
    !customerIds.some((id) => customerIdsMatch(row.customerId, id))
  ) {
    customerIds = [String(row.customerId)]
  }

  return {batchTab, batchId: filter.batchId, customerIds}
}

export function isBulkVosFinanceFilterActive(
  filter: BulkVosFinanceFilterData,
  batches: BulkVosFinanceBatchOption[],
): boolean {
  const baseline = buildDefaultFinanceFilter(batches)
  if (baseline.batchId == null) {
    return (
      filter.customerIds.length > 0 ||
      filter.batchTab !== 'pending' ||
      filter.batchId != null
    )
  }
  return !financeFiltersEqual(filter, baseline)
}

/** BulkVosBatchStatus: Open=0, ClientSubmitted=1, FinanceApproved=2, Expired=3 */
export function parseBulkVosBatchStatus(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (/^\d+$/.test(trimmed)) return Number(trimmed)
    const byName: Record<string, number> = {
      Open: 0,
      ClientSubmitted: 1,
      FinanceApproved: 2,
      Expired: 3,
    }
    if (byName[trimmed] != null) return byName[trimmed]
  }
  return 0
}

export function normalizeFinanceBatchRow(row: Record<string, unknown>): BulkVosFinanceBatchOption | null {
  const batchId = Number(row.batchId ?? row.BatchId ?? 0)
  const customerId = Number(row.customerId ?? row.CustomerId ?? 0)
  if (!batchId || !customerId) return null
  const disputedRaw = row.disputedCount ?? row.DisputedCount
  const createdRaw = row.createdAt ?? row.CreatedAt
  const submittedRaw = row.submittedAt ?? row.SubmittedAt
  return {
    batchId,
    customerId,
    customerName: (row.customerName ?? row.CustomerName) as string | undefined,
    disputedCount: disputedRaw != null ? Number(disputedRaw) : undefined,
    status: parseBulkVosBatchStatus(row.status ?? row.Status),
    createdAtUtc:
      createdRaw != null && String(createdRaw).trim() !== ''
        ? String(createdRaw)
        : undefined,
    submittedAtUtc:
      submittedRaw != null && String(submittedRaw).trim() !== ''
        ? String(submittedRaw)
        : undefined,
  }
}

export function normalizeFinanceBatchList(raw: unknown): BulkVosFinanceBatchOption[] {
  const arr = Array.isArray(raw) ? raw : []
  return arr
    .map((row) => normalizeFinanceBatchRow(row as Record<string, unknown>))
    .filter((b): b is BulkVosFinanceBatchOption => b != null)
}

export function batchesForFinanceTab(
  batches: BulkVosFinanceBatchOption[],
  tab: BulkVosFinanceBatchTab,
): BulkVosFinanceBatchOption[] {
  if (tab === 'approved') {
    return batches.filter((b) => b.status === 2)
  }
  return batches.filter((b) => b.status === 0 || b.status === 1)
}

export function customerIdsMatch(batchCustomerId: number, selectedCustomerId: string): boolean {
  return String(batchCustomerId) === String(selectedCustomerId).trim()
}

export function formatFinanceBatchLabel(b: BulkVosFinanceBatchOption): string {
  const client = (b.customerName ?? 'Client').trim() || 'Client'
  const dateRaw = b.submittedAtUtc ?? b.createdAtUtc
  const date = dateRaw ? formatSubmitted(dateRaw) : '—'
  return `${client} - ${date} - Batch[${b.batchId}]`
}

export function batchesForCustomers(
  batches: BulkVosFinanceBatchOption[],
  customerIds: string[],
): BulkVosFinanceBatchOption[] {
  if (!customerIds.length) return batches
  return batches.filter((b) =>
    customerIds.some((id) => customerIdsMatch(b.customerId, id)),
  )
}

export function applyBulkVosFinanceItemFilters(
  items: BatchItem[],
  _filter: BulkVosFinanceFilterData,
): BatchItem[] {
  return items
}

export function filterBulkVosBatchesByCustomer<T extends {customerId: number}>(
  batches: T[],
  customerIds: string[],
): T[] {
  if (!customerIds.length) return batches
  return batches.filter((b) =>
    customerIds.some((id) => customerIdsMatch(b.customerId, id)),
  )
}
